<template>
  <div
    class="smart-text-input"
    :style="{ backgroundColor: backgroundColor }"
    v-on:keydown.esc.exact.stop="e => $emit('cancel')"
    v-on:keydown.enter.exact="handleEnterKeyPress"
  >
    <div :id="id"></div>
    <div
      class="bottom-bar"
      v-if="!readonly && (showEmojisButton || showSubmitButton)"
    >
      <span>
        <i
          v-if="showEmojisButton"
          class="emojis-button fas fa-smile"
          @click="toggleEmojisPanelVisibility"
        ></i>
      </span>
      <div
        @click="submit"
        class="submit-container d-flex align-items-center text-primary"
      >
        <i
          v-if="showSubmitButton"
          class="submit-button fab fa-telegram-plane"
        ></i>
        <span v-if="showSubmitButton" style="font-size:13px;margin-left: 3px;"
          >Post</span
        >
      </div>
      <div
        class="emojis-panel"
        v-show="emojisVisible"
        v-click-outside="hideEmojisPanel"
        :style="[emojisBelow ? { top: '10px' } : { bottom: '10px' }]"
      >
        <b-input
          v-model="emojiSearch"
          placeholder="Search..."
          autocomplete="off"
          class="emojis-search-input"
        />
        <div class="emojis-search-results">
          <span
            v-for="emoji in emojiSearchResult"
            :key="emoji.value"
            class="emoji"
            @click="insertEmoji(emoji.value)"
          >
            {{ emoji.value }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import emojiSet from "emoji-set/src/all.json";
import userServer from "../../server/user";
import avatarServer from "../../server/avatar";

export default {
  name: "SmartTextInput",
  data() {
    return {
      id: `summernote_${new Date().getTime()}`,
      summernote: null,
      emojisVisible: false,
      emojisBelow: true,
      emojiSearch: "",
      emojis: [],
      range: null,
      users: [],
      hintShow: false
    };
  },
  props: {
    value: {
      type: String
    },
    mentions: {
      type: Array,
      default: () => []
    },
    showEmojisButton: {
      type: Boolean,
      default: true
    },
    showSubmitButton: {
      type: Boolean,
      default: true
    },
    backgroundColor: {
      type: String,
      default: "#F5F8FC"
    },
    readonly: {
      type: Boolean
    },
    focus: {
      type: Boolean
    },
    enableMentions: {
      type: Boolean,
      default: true
    },
    maxLength: {
      type: Number
    },
    placeholder: {
      type: String
    },
    showFullToolBar: {
      type: Boolean,
      default: false
    },
    showListOptions: {
      type: Boolean,
      default: true
    }
  },
  mounted() {
    const fullToolBar = [
      ["style", ["style", "bold", "italic", "underline", "clear"]],
      ["font", ["strikethrough", "superscript", "subscript"]],
      ["fontsize", ["fontsize"]],
      ["color", ["color"]],
      ["para", ["ul", "ol", "paragraph"]],
      ["table", ["table"]]
    ];
    const restrictedToolBar = [
      ["style", ["bold", "italic", "underline"]],
      ["color", ["color"]]
    ];
    if (this.showListOptions) {
      restrictedToolBar.push(["para", ["ul", "ol"]]);
    }
    const config = {
      dialogsInBody: true,
      disableDragAndDrop: true,
      placeholder: this.placeholder ? this.placeholder : "Write a note",
      focus: false,
      toolbar: this.showFullToolBar ? fullToolBar : restrictedToolBar,
      callbacks: {
        onChange: this.onChange,
        onKeyup: this.updateRange,
        onMouseup: this.updateRange,
        onHintSelected: this.onHintSelected,
        onHintShow: this.onHintShow,
        onHintHide: this.onHintHide,
        onImageUpload: data => this.onImageUpload(data)
      },
      ...(this.enableMentions
        ? { hint: this.hintConfig, hintDirection: "top" }
        : {}),
      ...(this.maxLength ? { maxTextLength: this.maxLength } : {}),
      lang: "en-US"
    };

    setTimeout(() => {
      const domEl = window.$("#" + this.id);
      this.summernote = domEl.summernote.bind(domEl);
      this.summernote(config);
      this.setText(this.value);
      this.setReadonly(this.readonly);

      if (this.focus) {
        this.summernote("focus");
      }
      this.emojis = Object.keys(emojiSet).map(e => ({
        value: e,
        ...emojiSet[e]
      }));

      if (this.showFullToolBar) {
        this.$el.querySelector(".note-editable").style.minHeight = "200px";
      }
    });

    this.fetchUsers();
  },
  beforeDestroy() {
    this.summernote("destroy");
  },
  methods: {
    async fetchUsers() {
      const users = await userServer.getMinimalUsers();
      this.users = users
        .map(user => ({
          value: user.id,
          label: this.formatUser(user),
          data: user
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

      this.users.forEach(user => {
        user.data.avatar = require(`../../assets/avatar-example.png`);
        if (user.data.avatarId) {
          avatarServer.downloadUrl(user.data.avatarId).then(response => {
            user.data.avatar = response;
          });
        }
      });
    },
    setText(value) {
      this.summernote("code", value || "");
      this.placeCaretAtTheEnd();
    },
    getText() {
      return this.summernote("code")
        .replace(/^&nbsp;+|&nbsp;+$/gm, "")
        .trim();
    },
    insertEmoji(emoji) {
      if (this.range) {
        this.range.deleteContents();
        this.range.collapse(true);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(this.range);
      } else {
        this.placeCaretAtTheEnd();
      }

      this.summernote("insertText", emoji);
      this.emojisVisible = false;
      this.updateRange();
    },
    toggleEmojisPanelVisibility() {
      if (!this.emojisVisible) {
        setTimeout(() => {
          this.emojisVisible = true;
          const rect = this.$el.getBoundingClientRect();
          if (rect.y + rect.height + 240 >= window.innerHeight) {
            this.emojisBelow = false;
          } else {
            this.emojisBelow = true;
          }
        });
      } else {
        this.emojisVisible = false;
      }
    },
    hideEmojisPanel() {
      this.emojisVisible = false;
    },
    placeCaretAtTheEnd() {
      const el = document.querySelector(
        `#${this.id} + .note-editor .note-editable`
      );

      if (el) {
        const range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
      }
    },
    updateRange() {
      const sel = window.getSelection();
      this.range = sel.rangeCount ? sel.getRangeAt(0) : null;
    },
    onChange() {
      if (this.value == this.getText()) {
        return;
      }

      this.updateMentions();
      this.emit();
    },
    onHintSelected() {
      const mentionNode = this.findNewMention();
      const whiteSpace = document.createTextNode("\u00a0");
      mentionNode.after(whiteSpace);
      const range = document.createRange();
      range.setStartAfter(whiteSpace);
      range.collapse(true);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);

      this.addMention(mentionNode.attributes["data-user"].value);
      this.emit();
    },
    findNewMention() {
      const oldFragment = document
        .createRange()
        .createContextualFragment(this.value);
      const oldMentions = Array.from(
        oldFragment.querySelectorAll("span[data-user]")
      );

      const newFragment = document
        .createRange()
        .createContextualFragment(this.summernote("code"));
      const newMentions = Array.from(
        newFragment.querySelectorAll("span[data-user]")
      );

      const mention = newMentions.find(
        item =>
          !oldMentions.some(
            old =>
              old.attributes["data-user"].value ==
                item.attributes["data-user"].value &&
              old.attributes["data-date"].value ==
                item.attributes["data-date"].value
          )
      );

      const userId = mention.attributes["data-user"].value;
      const date = mention.attributes["data-date"].value;
      return this.$el.querySelector(
        `span[data-user="${userId}"][data-date="${date}"]`
      );
    },
    addMention(userId) {
      const mentions = [...this.mentions, userId].filter(
        (item, i, arr) => arr.indexOf(item) == i
      );
      this.$emit("update:mentions", mentions);
    },
    updateMentions() {
      const mentionsInText = Array.from(
        this.$el.querySelectorAll("span[data-user]")
      )
        .map(node => node.attributes["data-user"].value)
        .filter((item, i, arr) => arr.indexOf(item) == i);
      const mentions = this.mentions.filter(m => mentionsInText.includes(m));
      this.$emit("update:mentions", mentions);
    },
    emit() {
      this.$emit("input", this.getText());
    },
    submit() {
      const text = this.getText();
      const notEmptyLine = text.replace(/<p><br><\/p>/, "").length;
      if (text && text.length && notEmptyLine) {
        this.$emit("submit", text);
      }
    },
    setReadonly(value) {
      const command = value ? "disable" : "enable";
      this.summernote(command);

      const display = value ? "none" : "block";
      this.$el.querySelector(".note-toolbar").style.display = display;
    },
    onHintShow() {
      this.hintShow = true;
    },
    onHintHide() {
      setTimeout(() => {
        this.hintShow = false;
      }, 100);
    },
    onImageUpload(data) {
      data.pop();
    },
    handleEnterKeyPress() {
      if (this.showSubmitButton && !this.hintShow) {
        const text = this.getText();
        this.setText(text.substring(0, text.length - 11));
        this.submit();
      }
    }
  },
  computed: {
    emojiSearchResult() {
      return this.emojis.filter(item =>
        item.keywords.some(k => k.includes(this.emojiSearch))
      );
    },
    hintConfig() {
      return {
        match: /\B@(\w*)$/,
        search: (keyword, callback) => {
          return callback(
            this.users.filter(item =>
              item.label.toLowerCase().includes(keyword.toLowerCase())
            )
          );
        },
        template: item => {
          return `
            <div class="d-flex align-items-center py-1 pr-2 pl-1" style="width: 272px;">
              <img src="${item.data.avatar}" width="25" style="margin-right:9px;margin-left: 3px;"/> ${item.label}
            </div>`;
        },
        content: item => {
          const tag = document.createElement("span");
          tag.innerText = `@${item.label}`;
          tag.setAttribute("data-user", item.value);
          tag.setAttribute("data-date", new Date().getTime());
          tag.setAttribute("contenteditable", "false");
          tag.classList.add("mention");

          return tag;
        }
      };
    }
  },
  watch: {
    value(newValue) {
      if (newValue != this.getText()) {
        this.setText(newValue);
      }
    },
    readonly(newValue) {
      this.setReadonly(newValue);
    }
  }
};
</script>

<style lang="scss">
$color-transition: color 150ms ease;

@mixin hoverable {
  cursor: pointer;
  transition: $color-transition;
  &:hover {
    color: #4dabf5;
  }
}

.smart-text-input {
  padding: 6px 6px;
  border-radius: 12px;
}

.note-editor.note-frame {
  border: none;

  .note-toolbar {
    background-color: transparent;
    padding: 0 6px 8px;
    border: none;

    .note-btn-group {
      padding-right: 4px;
      margin-top: 0;
      border-right: 1px solid black;

      &:first-of-type button:first-of-type {
        padding-left: 0;
      }

      &:last-of-type {
        border-right: none;
      }

      button {
        @include hoverable;

        background-color: transparent;
        padding: 0 5px;
        border: none;
      }

      .note-current-color-button {
        display: none;
      }

      .dropdown-toggle {
        margin-right: 0px;

        .note-icon-caret:before {
          content: "";
        }

        &:after {
          display: none;
        }
      }
    }
  }

  .note-editing-area {
    font-family: "Roboto", sans-serif;
    padding: 2px 2px 0;

    .note-placeholder {
      font-style: italic;
      padding: 0 4px 0;
    }
    .note-editable {
      max-height: 120px;
      padding: 0 4px 0;

      p {
        margin-bottom: 0.5rem;

        &:last-of-type {
          margin-bottom: 0;
        }
      }
    }
  }

  .note-statusbar {
    display: none;
  }
}

.bottom-bar {
  position: relative;
  display: flex;
  justify-content: space-between;
  line-height: 1;
  padding: 10px 6px 0;

  .emojis-button {
    @include hoverable;

    color: #707070;
    font-size: 17px;
  }
  .submit-container {
    @include hoverable;
  }
  .submit-button {
    font-size: 18px;
    padding-right: 2px;
  }
}

.emojis-panel {
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: absolute;
  left: 40px;
  max-height: 240px;
  background-color: white;
  border: 1px solid #ccc;
  z-index: 10;

  .emojis-search-input {
    padding-bottom: 0;
    border: none;
    border-radius: 0;

    &:focus {
      box-shadow: none;
    }
  }

  .emojis-search-results {
    display: grid;
    grid-template-rows: repeat(auto-fit, 20px);
    grid-template-columns: repeat(8, 20px);
    gap: 8px;
    padding: 8px;
    overflow: auto;

    &::-webkit-scrollbar {
      position: absolute;
    }
  }

  .emoji {
    cursor: pointer;
    justify-self: center;
  }
}
</style>
